/** WELDING PROJECT PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

export const PRODUCT = {
  name: 'WELDING PROJECT PLANS™',
  descriptor: 'Workshop Equipment Build Plans for DIY Welders & Fabricators',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for your own workshop—or build and sell the finished products.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 WORKSHOP PROJECTS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

const PAGE_SIZE = { width: 1688, height: 2184 }

export const HERO_MOCKUP: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/2gD64s8jy6piRZCKFczcfe/Mock_up_landing.png',
  alt: 'WELDING PROJECT PLANS™ collection: six numbered volumes, a tablet showing the P08 dimension plans, a printed cut-list sheet and a 5 free bonus badge',
  width: 1130,
  height: 1410,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Workshop Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/dvUiJxqbqfqZK2T2T88VBj/P08_01_V02_Page_09_Finished_Workbench.png',
    caption: '01 — Finished Workbench',
    alt: 'Volume 02, page 9: P08 Steel-Top Fabrication Workbench, finished workbench with technical inset',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/8qVLKBCjzZobQweaBMX2uK/P08_02_V02_Page_10_Dimension_Plans.png',
    caption: '02 — Dimensions & Technical Views',
    alt: 'Volume 02, page 10: P08 orthographic dimension plans with front, right, top and isometric views',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/hncn9XSDwAjGgot2w9kJAx/P08_03_V02_Page_11_Exploded_View_and_Cut_List.png',
    caption: '03 — Exploded View & Cut List',
    alt: 'Volume 02, page 11: P08 exploded view with lettered parts and the cut list table',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ekAbXcoRD8daSn9fMhT1u2/P08_04_V02_Page_12_Fabrication_Sequence.png',
    caption: '04 — Fabrication Sequence',
    alt: 'Volume 02, page 12: P08 seven-step illustrated fabrication sequence',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/wR7oWiS8guDP4gACjPpW3i/P08_05_V02_Page_13_Connection_and_Shelf_Details.png',
    caption: '05 — Connection & Shelf Details',
    alt: 'Volume 02, page 13: P08 connection details, shelf cut plan and part interfaces table',
    ...PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7aSGPK9oC2Vf1anfdWVRaN/P08_06_V02_Page_14_Finished_Assembly.png',
    caption: '06 — Finished Assembly',
    alt: 'Volume 02, page 14: P08 finished assembly reference with finished dimensions, parts list and final checks',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = [
  'Plan the materials.',
  'See how the parts fit.',
  'Follow the fabrication sequence.',
]

const V01_PAGE_SIZE = { width: 918, height: 1188 }

export const TECH_PROOF: TechProof[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/8DfYvLQdden4wwnUGFfNd3/01_P02_V01_p10_Fabrication_Support_Trestle_Pair_Dimensions.png',
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 02 · Fabrication Support Trestle Pair · Volume 01',
    description: 'Inspect the finished dimensions before cutting and assembly.',
    alt: 'Volume 01, page 10: Project 02 Fabrication Support Trestle Pair, finished dimensions of each trestle — 30 in wide, 18 in deep, 28.125 in high',
    ...V01_PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ugwvuGDk2kDGk33whJgw8h/02_P05_V01_p23_Welding_Lead_and_Torch_Parking_Pedestal_Cut_List.png',
    title: 'KNOW WHAT TO CUT',
    project: 'Project 05 · Welding Lead & Torch Parking Pedestal · Volume 01',
    description: 'Review the parts, quantities, stock, and cut sizes.',
    alt: 'Volume 01, page 23: Project 05 Welding Lead & Torch Parking Pedestal, complete cut list for parts A to E with arm and lip detail',
    ...V01_PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/gR6Wafp3tJsqn7rmBo3Zp5/03_P03_V01_p34_Adjustable_Material_Roller_Stand_Assembly_Detail.png',
    title: 'SEE HOW IT FITS',
    project: 'Project 03 · Adjustable Material Roller Stand · Volume 01',
    description: 'See the roller-head components and assembly details.',
    alt: 'Volume 01, page 34: Project 03 Adjustable Material Roller Stand, roller head assembly detail with brackets, roller and pin insertion',
    ...V01_PAGE_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/m1u9doMJ8koZ7SsJcRsL8K/04_P04_V01_p20_Freestanding_Welding_Screen_Frame_Build_Sequence.png',
    title: 'FOLLOW THE BUILD',
    project: 'Project 04 · Freestanding Welding Screen Frame · Volume 01',
    description: 'Follow the illustrated sequence from cut parts to assembled frame.',
    alt: 'Volume 01, page 20: Project 04 Freestanding Welding Screen Frame, eight-step build sequence from cut parts to finished frame',
    ...V01_PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1254, height: 1254 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Workshop Essentials & Support Gear',
    projects: '6 projects',
    description: 'Pedestals, trestles, roller supports, and practical workshop support equipment.',
    src: 'https://cdn.magicpatterns.com/uploads/nyeGZrVxf2drS23vcJ1tuK/Vol_1.png',
    alt: 'Volume 01 cover: Workshop Essentials & Support Gear',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Welding Tables & Workbenches',
    projects: '5 projects',
    description: 'Fixed welding tables and steel workbenches for fabrication.',
    src: 'https://cdn.magicpatterns.com/uploads/ptitaawA7Tjtz9juQgZcZ4/Vol_2.png',
    alt: 'Volume 02 cover: Welding Tables & Workbenches',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Welding Carts & Mobile Stations',
    projects: '5 projects',
    description: 'Equipment carts and mobile stations for welding and preparation.',
    src: 'https://cdn.magicpatterns.com/uploads/6nLfaBQUKFzsAH5TCCZnMj/Vol_3.png',
    alt: 'Volume 03 cover: Welding Carts & Mobile Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Storage & Material Racks',
    projects: '5 projects',
    description: 'Organize steel stock, sheet metal, offcuts, clamps, and hand tools.',
    src: 'https://cdn.magicpatterns.com/uploads/sEEDkw5UCwWz4u3xubcSbv/Vol_4.png',
    alt: 'Volume 04 cover: Storage & Material Racks',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Shop Tool & Preparation Stations',
    projects: '5 projects',
    description: 'Dedicated stations for cutting, grinding, drilling, layout, and finishing.',
    src: 'https://cdn.magicpatterns.com/uploads/fMxX2nUXeHHHBkc18EeBKK/Vol_5.png',
    alt: 'Volume 05 cover: Shop Tool & Preparation Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Integrated Workshop Systems',
    projects: '4 projects',
    description: 'Combine work zones, preparation, organization, and material handling.',
    src: 'https://cdn.magicpatterns.com/uploads/kpqxmvrKdmZvsRVpKc9nFD/Vol_6.png',
    alt: 'Volume 06 cover: Integrated Workshop Systems',
    ...COVER_SIZE,
  },
]

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Project Worksheet',
    description: 'Organize your steel purchases and project notes.',
    src: 'https://cdn.magicpatterns.com/uploads/fvziMcDkz7x88EdyTu2zDF/Bonus_01_Steel_Purchase_Project_Worksheet.png',
    alt: 'Bonus 01 cover: Steel Purchase & Project Worksheet',
    ...COVER_SIZE,
  },
  {
    number: '02',
    title: 'Cut Optimization & Part Labeling Sheet',
    description: 'Plan your cutting layout and label parts before assembly.',
    src: 'https://cdn.magicpatterns.com/uploads/szM4A6wiuo1mXbCRAuMmfr/Bonus_02_Cut_Optimization_Part_Labeling_Sheet.png',
    alt: 'Bonus 02 cover: Cut Optimization & Part Labeling Sheet',
    ...COVER_SIZE,
  },
  {
    number: '03',
    title: 'Workshop Safety Field Card',
    description: 'Keep a concise workshop checklist close at hand.',
    src: 'https://cdn.magicpatterns.com/uploads/h4kGKUxkL9P2isGDDapyuv/Bonus_03_Workshop_Safety_Field_Card.png',
    alt: 'Bonus 03 cover: Workshop Safety Field Card',
    ...COVER_SIZE,
  },
  {
    number: '04',
    title: 'Surface Prep & Finishing Guide',
    description: 'Organize surface preparation and finishing steps.',
    src: 'https://cdn.magicpatterns.com/uploads/eZ1CSWhxnaZRqEMK7tYaGQ/Bonus_04_Surface_Prep_Finishing_Guide.png',
    alt: 'Bonus 04 cover: Surface Prep & Finishing Guide',
    ...COVER_SIZE,
  },
  {
    number: '05',
    title: 'Project Costing Worksheet',
    description: 'Track material costs and build your project estimate.',
    src: 'https://cdn.magicpatterns.com/uploads/29j8qnLAVRh1UkntEddJKx/Bonus_05_Project_Costing_Worksheet.png',
    alt: 'Bonus 05 cover: Project Costing Worksheet',
    ...COVER_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  'Dimensions and technical views — inch-based plans with metric conversion references.',
  'Material and cut-list information',
  'Exploded views and fabrication guidance',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of workshop equipment build plans: 30 projects across six PDF volumes, plus five additional resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'DIY welders with fabrication experience, independent fabricators, and small workshops. It is intended for people who already know how to weld and work with steel.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'The collection focuses on workshop equipment projects and fabrication documentation. It does not teach welding from the beginning.',
  },
  {
    question: 'Are these physical books or equipment?',
    answer:
      'The product is digital. You receive PDF files; physical books, steel, tools, and equipment are not included.',
  },
  {
    question: 'Can I view the files on a tablet or computer?',
    answer:
      'Yes. You can open the PDFs on a compatible computer, tablet, or phone, and print the pages you need.',
  },
  {
    question: 'What kinds of projects are included?',
    answer:
      'Workshop support equipment, welding tables and workbenches, equipment carts, material racks, tool stations, and integrated workshop systems.',
  },
  {
    question: 'Can I preview the plans?',
    answer:
      'Yes. The complete six-page Steel-Top Fabrication Workbench project is available in the preview section above.',
  },
  {
    question: 'Can I sell the products I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own workshop or to sell the physical products you make. You may print working copies for your own use. You may not share, resell, redistribute, or sublicense the digital plans, PDFs, images, or bonus files, whether original or modified.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'
